import { useRef, useState } from 'react'
import type { KeyboardEvent } from 'react'
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useGuidedTourStore } from '@/stores/guidedTourStore'
import { useModalA11y } from '@/shared/hooks/useModalA11y'
import { AjudaComandos } from './AjudaComandos'
import { AjudaJornada } from './AjudaJornada'
import { AjudaPassos } from './AjudaPassos'
import './ajuda-do-tour.css'

const ABAS = ['passos', 'comandos', 'jornada'] as const
type Aba = (typeof ABAS)[number]

function proximaAba(atual: Aba, key: string): Aba | null {
  const i = ABAS.indexOf(atual)
  if (key === 'ArrowRight') return ABAS[(i + 1) % ABAS.length] ?? null
  if (key === 'ArrowLeft') return ABAS[(i - 1 + ABAS.length) % ABAS.length] ?? null
  if (key === 'Home') return ABAS[0]
  if (key === 'End') return ABAS[ABAS.length - 1] ?? null
  return null
}

function Painel({ aba }: { aba: Aba }) {
  if (aba === 'comandos') return <AjudaComandos />
  if (aba === 'jornada') return <AjudaJornada />
  return <AjudaPassos />
}

function CentralDeAjuda() {
  const { t } = useTranslation()
  const [aba, setAba] = useState<Aba>('passos')
  const dialogRef = useRef<HTMLDivElement>(null)
  const abaRef = useRef<HTMLButtonElement>(null)
  const fechar = () => useGuidedTourStore.getState().fecharAjuda()
  useModalA11y({ active: true, dialogRef, firstFocusRef: abaRef, onClose: fechar })

  function aoTeclar(e: KeyboardEvent<HTMLDivElement>) {
    const destino = proximaAba(aba, e.key)
    if (!destino) return
    e.preventDefault()
    setAba(destino)
    document.getElementById(`ajuda-aba-${destino}`)?.focus()
  }

  return (
    <div className="axg-ajuda-fundo" onClick={fechar}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ajuda-titulo"
        data-testid="ajuda-do-tour"
        className="axg-ajuda-janela"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="axg-ajuda-cabeca">
          <h2 id="ajuda-titulo" className="axg-ajuda-titulo">
            {t('guidedTour.ajuda.titulo')}
          </h2>
          <button type="button" className="axg-ajuda-fechar" onClick={fechar} aria-label={t('guidedTour.ajuda.fechar')}>
            <X size={18} aria-hidden="true" />
          </button>
        </div>
        <div role="tablist" aria-label={t('guidedTour.ajuda.abasRotulo')} className="axg-ajuda-abas" onKeyDown={aoTeclar}>
          {ABAS.map((id) => (
            <button
              key={id}
              ref={id === aba ? abaRef : undefined}
              type="button"
              role="tab"
              id={`ajuda-aba-${id}`}
              aria-selected={id === aba}
              aria-controls={`ajuda-painel-${id}`}
              tabIndex={id === aba ? 0 : -1}
              data-testid={`ajuda-tab-${id}`}
              className="axg-ajuda-aba"
              onClick={() => setAba(id)}
            >
              {t(`guidedTour.ajuda.abas.${id}`)}
            </button>
          ))}
        </div>
        <div role="tabpanel" id={`ajuda-painel-${aba}`} aria-labelledby={`ajuda-aba-${aba}`} className="axg-ajuda-painel">
          <Painel aba={aba} />
        </div>
      </div>
    </div>
  )
}

/** Central de Ajuda: modal sobre a tela atual, aberto por `ajudaAberta` (nunca rota). */
export function AjudaDoTour() {
  const aberta = useGuidedTourStore((s) => s.ajudaAberta)
  return aberta ? <CentralDeAjuda /> : null
}
